import { Avatar, Box, Button, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { CustomPopover, enqueueSnackbar } from '@zeta-core';
import { EyeIcon, PlusIcon } from '@zeta-core/assets';
import { AdvisorAPIs, getErrorMessage } from '@zeta-services';
import { IAdvisorUser, IGenericErrorResponse, IGetAdvisorUserListRequest, IGetAdvisorUserListResponse } from '@zeta-types';

import { AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

// Helper function to get initials from name
export const getInitials = (name: string): string => {
  if (!name) return '';
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  return name[0]?.toUpperCase() || '';
};

export const AdvisorAccessInfo = ({
  canWrite = false,
  handleShowManageAccess,
  handleShowAddAccess,
  advisorUserList,
  setAdvisorUserList,
  handleClosePopover,
  open,
  anchorEl,
}: {
  canWrite: boolean;
  handleShowManageAccess: () => void;
  handleShowAddAccess: () => void;
  advisorUserList: IGetAdvisorUserListResponse;
  setAdvisorUserList: (advisorUserList: IAdvisorUser[]) => void;
  handleClosePopover: () => void;
  open: boolean;
  anchorEl: HTMLElement | null;
}) => {
  const location = useLocation();
  const caseID = location.state?.clientData?.caseID;
  const theme = useTheme();
  const getAdvisorUserList = useMutation({
    mutationFn: (payload: IGetAdvisorUserListRequest) => AdvisorAPIs().getAdvisorUserList(payload),
    onSuccess: (response: AxiosResponse<IGetAdvisorUserListResponse>) => {
      setAdvisorUserList(response?.data?.advisorUsers);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (caseID) {
      getAdvisorUserList.mutate({ caseID });
    }
  }, [caseID]);

  // Get the users to display
  const users = advisorUserList || [];
  const displayUsers = users?.slice?.(0, 2);
  const remainingCount = Math.max(0, users.length - 2);
  return (
    <CustomPopover anchorEl={anchorEl} open={Boolean(open)} onClose={handleClosePopover} sx={{ px: 2, py: 1 }}>
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
        <Stack flexDirection="row" gap={1} justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Access:
          </Typography>
          {users.length > 0 && (
            <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
              {/* First avatar (if available) */}
              {displayUsers.length > 0 && (
                <Tooltip title={displayUsers[0].fullName} placement="top" arrow>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: theme.palette.grey[300],
                      color: theme.palette.text?.secondary,
                      fontWeight: 500,
                      fontSize: '14px',
                      border: `3px solid ${theme.palette.common.white}`,
                      zIndex: 3,
                    }}
                  >
                    {getInitials(displayUsers[0].fullName)}
                  </Avatar>
                </Tooltip>
              )}

              {/* Second avatar (if available) */}
              {displayUsers.length > 1 && (
                <Tooltip title={displayUsers[1].fullName} placement="top" arrow>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: theme.palette.grey[300],
                      color: theme.palette.text?.secondary,
                      fontWeight: 500,
                      fontSize: '14px',
                      marginLeft: '-8px',
                      border: `3px solid ${theme.palette.common.white}`,
                      zIndex: 2,
                    }}
                  >
                    {getInitials(displayUsers[1].fullName)}
                  </Avatar>
                </Tooltip>
              )}

              {/* Additional users indicator (only if there are more than 2 users) */}
              {remainingCount > 0 && (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '-8px',
                    borderRadius: '50%',
                    bgcolor: theme.palette.grey[100],
                    color: theme.palette.primary.main,
                    border: `3px solid ${theme.palette.common.white}`,
                    fontWeight: 600,
                    fontSize: '14px',
                    zIndex: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    +{remainingCount}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
        <Stack flexDirection="row" gap={1} alignItems="center">
          <Tooltip title="View All" placement="bottom" arrow>
            <IconButton color="primary" sx={{ svg: { width: '20px', height: '20px' } }} onClick={() => handleShowManageAccess()}>
              <EyeIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Access" placement="bottom" arrow>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              disabled={!canWrite}
              sx={{ minWidth: 'unset !important', minHeight: 'unset !important', width: 30, height: 30, p: 0 }}
              onClick={() => handleShowAddAccess()}
            >
              <PlusIcon />
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </CustomPopover>
  );
};
